import { Location } from '@angular/common';
import { Component, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { CustomUserDropdownItem } from '@galaxy/atlas';
import { Environment, EnvironmentService } from '@galaxy/core';
import { VendorService } from '@galaxy/marketplace-apps/v1';
import { BrandingV2Service, FeatureFlagService, FeatureFlagStatusInterface } from '@galaxy/partner';
import { TranslateService } from '@ngx-translate/core';
import { GalaxyNavControlService } from '@vendasta/galaxy/nav';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { IAMService, User as UserV2, UserIdentifier as UserIdentifierV2 } from '@vendasta/iamv2';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { filter, map, shareReplay, startWith, switchMap, take } from 'rxjs/operators';
import { AuthService, User } from './core/auth/auth.service';
import { FeatureFlags } from './core/feature-flags';
import { HotJarService } from './core/hotjar.service';
import { MarketplaceAppService } from './core/marketplace-app/marketplace-app.service';
import { OmniSearchComponent } from './features/omni-search/omni-search.component';
import { OmniSearchService } from './features/omni-search/omni-search.service';
import { PartnerSwitcherComponent } from './features/omni-search/partner-switcher/partner-switcher.component';
import { ProductSearchComponent } from './features/omni-search/product-search/product-search.component';
import { PARTNER_ID } from './tokens';

declare const deployment: string; // Stamped down by vStatic
declare const window: any;

const POST_HOG_ID = 'oPmNKRPyKxZSquHb1h0aqRojbqlhh090pQiVR_9AAvk';
const POST_HOG_PROJECT_ID = 'db0deb3d-d77c-40dc-b9aa-61a39e21e375';
const POST_HOG_PROJECT_NAME = 'vendor-center-client';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  isSuperAdmin$: Observable<boolean>;
  isAuthenticated$: Observable<boolean>;
  featureFlags$: Observable<FeatureFlagStatusInterface>;
  userEmail$: Observable<string>;
  appId$: Observable<string>;
  atlasTitle$: Observable<string>;
  whitelabelLogoUrl$: Observable<string>;
  inboxEnabled$: Observable<boolean>;
  isVendorDistributed$: Observable<boolean>;
  crmFeatureFlags$: Observable<{ [key: string]: boolean }>;
  singleOrdersTableFeatureFlag$: Observable<boolean>;

  protected readonly FeatureFlags = FeatureFlags;

  omniSearchDialogRef: MatDialogRef<OmniSearchComponent>;
  atlasMenuItems: CustomUserDropdownItem[] = [];
  isOrdersPage: boolean;

  subscriptions: Subscription[] = [];

  constructor(
    @Inject(PARTNER_ID) public partnerId$: Observable<string>,
    private auth: AuthService,
    private marketplaceAppService: MarketplaceAppService,
    private router: Router,
    private snackbarService: SnackbarService,
    private hotJarService: HotJarService,
    private analytics: ProductAnalyticsService,
    private environmentService: EnvironmentService,
    private translateService: TranslateService,
    private brandingService: BrandingV2Service,
    private navControl: GalaxyNavControlService,
    private featureFlagService: FeatureFlagService,
    private vendorService: VendorService,
    private iamService: IAMService,
    private omniSearchService: OmniSearchService,
    private readonly location: Location,
    public dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    // Initialize the component variables
    this.isSuperAdmin$ = this.auth.isSuperAdmin$;
    this.appId$ = this.marketplaceAppService.currentAppId$;
    this.isAuthenticated$ = this.auth.isAuthenticated$;
    this.userEmail$ = this.auth.currentUser$.pipe(map((user) => (user != null ? user.email : null)));

    this.featureFlags$ = this.partnerId$.pipe(
      take(1),
      switchMap((partnerId) => {
        return this.featureFlagService.batchGetStatus(partnerId, 'default', [
          FeatureFlags.CRM_TAB,
          FeatureFlags.CRM_COMPANY_TAB,
          FeatureFlags.INBOX_TAB_PCC,
          FeatureFlags.SINGLE_ORDERS_TABLE,
        ]);
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
    this.singleOrdersTableFeatureFlag$ = this.featureFlags$.pipe(
      map((featureFlags) => featureFlags[FeatureFlags.SINGLE_ORDERS_TABLE]),
    );

    const currentUrl$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.urlAfterRedirects),
      startWith(this.location.path()),
    );
    this.subscriptions.push(
      currentUrl$
        .pipe(
          map((url) => {
            this.isOrdersPage = ['/orders', '/work-orders'].some((ordersPath) => url.includes(ordersPath));
          }),
        )
        .subscribe(),
    );

    const userV2$ = this.auth.currentUser$.pipe(
      filter<User>(Boolean),
      switchMap((user) =>
        this.iamService.getUser(
          new UserIdentifierV2({
            userId: user.userId,
          }),
        ),
      ),
    );

    // Initialize services & configurations
    if (typeof deployment !== 'undefined') {
      this.subscriptions.push(
        combineLatest([userV2$, this.partnerId$]).subscribe(([user, partnerId]) => {
          this.initializeUserOnboarding(user, partnerId);
          this.analytics.initialize({
            environment: this.environmentService.getEnvironment(),
            projectUUID: POST_HOG_PROJECT_ID,
            postHogID: POST_HOG_ID,
            projectName: POST_HOG_PROJECT_NAME,
            partner: {
              pid: partnerId,
            },
          });
        }),
      );
    }

    if (this.environmentService.getEnvironment() === Environment.PROD) {
      this.hotJarService.initialize('748867');
    }

    this.atlasMenuItems = [
      {
        label: 'Sign out',
        callback: this.navigateToLogout.bind(this),
      },
    ];

    this.translateService.setDefaultLang('en');

    // Load the partner branding
    const branding$ = this.partnerId$.pipe(
      switchMap((partnerId) => {
        return this.brandingService.getBranding(partnerId);
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.whitelabelLogoUrl$ = combineLatest([this.auth.currentUser$, branding$]).pipe(
      map(([user, branding]) => {
        if (!user || !branding) {
          return '';
        }
        return branding.logoUrl;
      }),
    );

    this.atlasTitle$ = combineLatest([this.auth.currentUser$, branding$, this.partnerId$]).pipe(
      map(([user, branding, partnerId]) => {
        if (!user) {
          return '';
        }
        return `${branding.name} (${partnerId})`;
      }),
    );

    this.inboxEnabled$ = this.featureFlags$.pipe(
      map((featureFlags) => featureFlags[FeatureFlags.INBOX_TAB_PCC]),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.isVendorDistributed$ = this.partnerId$.pipe(
      switchMap((partnerId) => this.vendorService.isVendorDistributed(partnerId)),
    );

    this.subscriptions.push(
      this.isVendorDistributed$.pipe(take(1)).subscribe((v) => {
        if (v) {
          this.analytics.trackProperties({ isDistributedVendor: true });
        }
      }),
    );

    this.crmFeatureFlags$ = this.featureFlags$.pipe(
      map((featureFlags) => {
        const map: { [key: string]: boolean } = {};
        map[FeatureFlags.CRM_TAB] = featureFlags[FeatureFlags.CRM_TAB];
        map[FeatureFlags.CRM_COMPANY_TAB] = featureFlags[FeatureFlags.CRM_COMPANY_TAB];
        return map;
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.omniSearchService.registerOptions([
      {
        title: 'Search Partners',
        description: "Switch Vendor Center's context to a given PID",
        shortcut: 'p',
        callback: () => {
          this.showPartnerSwitcherDialog();
        },
      },
      {
        title: 'Search Products',
        description: 'Go to the product details page for a product',
        shortcut: 'i',
        callback: () => {
          this.showProductSearchDialog();
        },
      },
    ]);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  private initializeUserOnboarding(user: UserV2, partnerId: string): void {
    // Determine the roles that the user can perform.
    // This will help Chameleon appropriately segment users and apply role-based filters.
    const permissions: { [role: string]: boolean } = {};
    Object.keys(user.roles).forEach((role) => (permissions[role] = true));
    window.chmln.identify(user.userId, {
      created: user.created,
      project: 'vendor-center-client',
      role_permissions: permissions,

      company: {
        uid: partnerId,
      },
    });
  }

  navigateToLogout(): void {
    this.subscriptions.push(
      this.auth
        .logout()
        .pipe(take(1))
        .subscribe({
          next: (nextUrl: string) => {
            if (nextUrl.startsWith('http')) {
              window.location.href = nextUrl;
            } else {
              this.router.navigate([nextUrl]);
            }
          },
          error: () => {
            this.snackbarService.openErrorSnack('We failed to log you out. Refresh the page to try again.');
          },
        }),
    );
  }

  toggle(): void {
    this.navControl.toggle();
  }

  @HostListener('window:keydown', ['$event'])
  showSearchDialog(event: KeyboardEvent): void {
    this.isSuperAdmin$.pipe(take(1)).subscribe((isSuperAdmin) => {
      if (isSuperAdmin) {
        const target = event.target as HTMLElement;
        const tag = target.tagName.toLowerCase();

        if (event.key === '/' && tag !== 'input' && tag !== 'textarea') {
          if (this.omniSearchDialogRef === null || this.omniSearchDialogRef === undefined) {
            this.omniSearchDialogRef = this.dialog.open(OmniSearchComponent, { width: '600px' });
            this.omniSearchDialogRef.afterClosed().subscribe(() => (this.omniSearchDialogRef = null));
          } else {
            this.omniSearchDialogRef.close();
          }
        }
      }
    });
  }

  showPartnerSwitcherDialog(): void {
    this.dialog.open(PartnerSwitcherComponent, { width: '600px' });
  }

  showProductSearchDialog(): void {
    this.dialog.open(ProductSearchComponent, { width: '600px' });
  }

  showPidSelector(): void {
    this.isSuperAdmin$.pipe(take(1)).subscribe((isSuperAdmin) => {
      if (isSuperAdmin) {
        this.showPartnerSwitcherDialog();
      }
    });
  }
}
